import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { deletePatient, listTherapistPatients } from "../../application/patients";
import { AlertTriangle, ChevronRight, Loader2, SearchX, Trash2, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router";
import Button from "../../../../shared/ui/button/Button";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { EmptyState } from "../../../../shared/ui/primitives";
import { useMotionSafe } from "../../../../hooks/useMotionSafe";

import { Patient } from "../../domain/Patient";
import { getAvatarColor, getInitials } from "../../../../shared/utils/avatar";
import { formatAcademicYear, useCenter, useCourseLabels } from "../../../center";
import { PatientOwnershipBadge } from "./PatientOwnership";

interface PatientsListProps {
  therapistID: string;
  filters?: {
    name?: string;
    /** A training centre's students: only this course, and this year of it. */
    courseId?: string;
    academicYear?: string;
  };
  pageSize?: number;
}

const SKELETON_ROWS = 3;

export default function PatientsList({ therapistID, filters, pageSize = 10 }: PatientsListProps) {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Asked about before it is deleted; the answer is a dialog, not the browser's.
  const [pendingDelete, setPendingDelete] = useState<Patient | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { container, item, transition, reduced } = useMotionSafe();

  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();
  // For a therapist in a centre, whose each patient is: the centre's (it sees
  // them) or their own. Meaningless for somebody who works alone, so hidden.
  const { center } = useCenter();
  // A training centre's student is enrolled once per course: which one this is.
  const { courseOf } = useCourseLabels();

  // Only the newest request may write to the list. A filter change fires one
  // fetch at the current offset and another once the offset resets to 0, and
  // the first can land last — leaving the new filter shown from the old page.
  const latestRequest = useRef(0);

  const fetchPatients = useCallback(async (searchTerm: string = "") => {
    if (!therapistID) return;
    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      // A retry starts clean: the old error must not outlive a good answer.
      setError(null);
      const res = await listTherapistPatients(therapistID, {
        offset,
        limit: pageSize,
        search: searchTerm || "",
        // A training centre's students, narrowed to a course and a year.
        ...(filters?.courseId ? { course_id: filters.courseId } : {}),
        ...(filters?.academicYear ? { academic_year: Number(filters.academicYear) } : {}),
      });
      if (requestId !== latestRequest.current) return;

      const data = res.patients;
      setPatients(data);
      setTotal(res.total);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error("Error loading patients:", err);
      setError(t("patient.errorLoading"));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [therapistID, offset, pageSize, t, filters?.courseId, filters?.academicYear]);

  useEffect(() => {
    fetchPatients(filters?.name || "");
  }, [fetchPatients, filters?.name]);

  // A new filter starts from the first page.
  useEffect(() => {
    setOffset(0);
  }, [filters?.name, filters?.courseId, filters?.academicYear]);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setDeletingId(id);
    setDeleteError(null);
    try {
      await deletePatient(id);

      // The last patient on a page that is not the first: step back a page.
      if (patients.length === 1 && offset > 0) {
        setOffset((prev) => Math.max(prev - pageSize, 0));
      } else {
        fetchPatients(filters?.name || "");
      }
    } catch (err) {
      console.error("Error deleting patient:", err);
      setDeleteError(t("patient.errorDeleting"));
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
    }
  };

  const handlePrevPage = () => setOffset((prev) => Math.max(prev - pageSize, 0));
  const handleNextPage = () => setOffset((prev) => prev + pageSize);

  const hasFilters = Boolean(filters?.name || filters?.courseId || filters?.academicYear);
  const fullName = (patient: Patient) => `${patient.first_name} ${patient.last_name}`.trim();
  // A student's course and year, under their name.
  const studyDetail = (patient: Patient) => {
    const course = courseOf(patient.course_id);
    if (!course) return null;
    return [course, patient.academic_year ? formatAcademicYear(patient.academic_year) : null]
      .filter(Boolean)
      .join(" · ");
  };

  if (loading) {
    return (
      <div role="status" className="grid grid-cols-1 gap-4">
        <span className="sr-only">{t("patient.loading")}</span>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <div
            key={index}
            aria-hidden="true"
            className="flex items-center gap-4 rounded-2xl border border-border-subtle p-4 motion-safe:animate-pulse"
          >
            <div className="size-12 shrink-0 rounded-full bg-surface-sunken" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 rounded-sm bg-surface-sunken" />
              <div className="h-3 w-1/2 rounded-sm bg-surface-sunken" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert">
        <EmptyState
          icon={<AlertTriangle className="size-5" />}
          title={error}
          action={
            <Button variant="outline" onClick={() => fetchPatients(filters?.name || "")}>
              {t("patient.retry")}
            </Button>
          }
        />
      </div>
    );
  }

  if (!patients.length) {
    // Nobody yet is a starting point; nobody matching is a filter to loosen.
    return hasFilters ? (
      <EmptyState
        icon={<SearchX className="size-5" />}
        title={t("patient.noMatchTitle")}
        description={t("patient.noMatchDescription")}
      />
    ) : (
      <EmptyState
        icon={<UserPlus className="size-5" />}
        title={t("patient.emptyTitle")}
        description={t("patient.emptyDescription")}
        action={
          <Button onClick={() => navigate("/patients/new")}>
            {t("patient.newPatient")}
          </Button>
        }
      />
    );
  }

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="space-y-4"
      data-tour="patient-overview"
    >
      {deleteError && (
        <p role="alert" className="rounded-lg bg-status-critical-tint p-3 text-sm text-status-critical-on-tint">
          {deleteError}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4">
        <AnimatePresence mode="popLayout">
          {patients.map((patient) => (
            <motion.div
              // Rows sliding into a deleted row's place are movement too.
              layout={!reduced}
              variants={item}
              transition={transition}
              key={patient.id}
              className="group relative flex flex-col sm:flex-row items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl shadow-theme-xs hover:border-brand-500/50 hover:shadow-raised transition-all duration-300 dark:bg-gray-900/40 dark:border-gray-800 dark:hover:border-brand-500/50"
            >
              <div className="flex flex-1 items-center gap-4 w-full">
                {/* Avatar */}
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 text-sm font-bold uppercase transition-transform group-hover:scale-110 ${getAvatarColor(
                    patient.id
                  )}`}
                >
                  {getInitials(patient.first_name, patient.last_name)}
                </div>

                {/* Patient Details */}
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/patient/${patient.id}`}
                    className="block group-hover:text-brand-500 transition-colors"
                  >
                    <h3 className="text-base font-semibold text-content-primary truncate">
                      {patient.first_name} {patient.last_name}
                    </h3>
                  </Link>
                  {studyDetail(patient) && (
                    <p className="mt-0.5 truncate text-sm font-medium text-accent-text">
                      {studyDetail(patient)}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1 text-sm text-content-tertiary">
                    <span className="truncate">{patient.contact_email || t("common.noEmail")}</span>
                    <span className="hidden sm:inline text-gray-300 dark:text-gray-700">•</span>
                    <span>{patient.contact_phone || t("common.noPhone")}</span>
                  </div>
                </div>
              </div>

              {center && (
                <PatientOwnershipBadge
                  heldByCenter={Boolean(patient.center_id)}
                  className="mt-3 sm:mt-0 sm:ml-4"
                />
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 mt-4 sm:mt-0 ml-auto sm:ml-4">
                <button
                  type="button"
                  className="inline-flex min-h-11 min-w-11 items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400 transition-all"
                  onClick={() => setPendingDelete(patient)}
                  disabled={deletingId === patient.id}
                  aria-busy={deletingId === patient.id || undefined}
                  aria-label={t("patient.deleteNamed", { name: fullName(patient) })}
                >
                  {deletingId === patient.id ? (
                    <Loader2 aria-hidden="true" className="w-5 h-5 motion-safe:animate-spin" />
                  ) : (
                    <Trash2 aria-hidden="true" className="w-5 h-5" />
                  )}
                </button>
                {/* The name is a link too; this is the target a thumb can hit. */}
                <Link
                  to={`/patient/${patient.id}`}
                  className="inline-flex min-h-11 min-w-11 items-center justify-center p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-brand-50 hover:text-brand-500 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 transition-all"
                  aria-label={t("patient.viewProfileOf", { name: fullName(patient) })}
                >
                  <ChevronRight aria-hidden="true" className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <Pagination
        offset={offset}
        total={total}
        pageSize={pageSize}
        onPrev={handlePrevPage}
        onNext={handleNextPage}
      />

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        title={t("patient.deletePatient")}
        message={pendingDelete ? t("patient.confirmDeleteNamed", { name: fullName(pendingDelete) }) : ""}
        confirmLabel={t("common.delete")}
        destructive
        isBusy={deletingId !== null}
        onConfirm={() => void handleDelete()}
        onCancel={() => {
          if (deletingId === null) setPendingDelete(null);
        }}
      />
    </motion.div>
  );
}
